/**
 * useMissionTrust
 *
 * Fetches the trust tier for a mission's primary agent.
 * Returns the tier for display in TrustBadge in the chat header.
 *
 * Resolution order:
 * 1. trustService.getTrustScore(primaryAgentId) — governance API
 * 2. mission.metadata.trust_tier or metadata.trustTier — embedded metadata
 * 3. null — trust data unavailable, badge should not render
 *
 * Caches result per missionId to avoid refetching on re-render.
 */

import { useEffect, useRef, useState } from 'react'
import { useMissionStore } from './useMissions'
import { trustService } from '../services/trust.service'
import type { TrustTier } from '@agion/ui'

interface UseMissionTrustResult {
  trustTier: TrustTier | null
  isLoading: boolean
}

/**
 * Clamp and round a raw tier value to a valid TrustTier (0-3).
 */
function clampTier(raw: number): TrustTier {
  return Math.max(0, Math.min(3, Math.round(raw))) as TrustTier
}

export function useMissionTrust(missionId: string): UseMissionTrustResult {
  const mission = useMissionStore((s) => s.getMissionById(missionId))

  // Cache: once resolved per missionId, do not re-fetch
  const cachedRef = useRef<Map<string, TrustTier | null>>(new Map())

  const [trustTier, setTrustTier] = useState<TrustTier | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Mission not in store yet
    if (!mission) {
      setTrustTier(null)
      setIsLoading(false)
      return
    }

    // Return cached result if available
    if (cachedRef.current.has(missionId)) {
      setTrustTier(cachedRef.current.get(missionId) ?? null)
      setIsLoading(false)
      return
    }

    const primaryAgentId = mission.primaryAgentId

    if (!primaryAgentId) {
      // No agent — fall back to metadata
      const meta = mission.metadata as Record<string, unknown> | undefined
      const raw =
        (meta?.['trust_tier'] as number | undefined) ??
        (meta?.['trustTier'] as number | undefined)

      const tier = raw != null ? clampTier(raw) : null
      cachedRef.current.set(missionId, tier)
      setTrustTier(tier)
      setIsLoading(false)
      return
    }

    // Fetch from governance API
    let cancelled = false
    setIsLoading(true)

    trustService
      .getTrustScore(primaryAgentId)
      .then((score) => {
        if (cancelled) return
        const tier = score != null ? clampTier(score.trustTier) : null
        cachedRef.current.set(missionId, tier)
        setTrustTier(tier)
        setIsLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        cachedRef.current.set(missionId, null)
        setTrustTier(null)
        setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [missionId, mission])

  return { trustTier, isLoading }
}
